/**
 * Speech Synthesis via the AI gateway
 *
 * Asks the gateway's `tts` role for speech audio, then plays it via
 * Web Audio API (AudioContext). Works in immersive WebXR on Quest where
 * the Web Speech API's SpeechSynthesis is unavailable.
 */

import { callCapability } from './gateway';
import type { VoiceTiming } from './voice-timing';

/** PCM format returned by Gemini TTS */
const SAMPLE_RATE = 24000;
const NUM_CHANNELS = 1;

/**
 * Decode a base64-encoded little-endian int16 PCM buffer into a Float32Array
 * normalized to [-1, 1]. Exposed for unit testing; throws on odd byte count.
 */
export function decodePcm16ToFloat32(base64Pcm: string): Float32Array {
    const binaryString = atob(base64Pcm);
    const pcmBytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        pcmBytes[i] = binaryString.charCodeAt(i);
    }
    if (pcmBytes.length % 2 !== 0) {
        throw new Error(`PCM byte length must be even, got ${pcmBytes.length}`);
    }
    const view = new DataView(pcmBytes.buffer);
    const numSamples = pcmBytes.length / 2;
    const out = new Float32Array(numSamples);
    for (let i = 0; i < numSamples; i++) {
        out[i] = view.getInt16(i * 2, true) / 32768;
    }
    return out;
}

/**
 * One synthesis result, whichever provider produced it. `mimeType` is the
 * field that makes the two interchangeable: Azure returns a compressed
 * 'audio/mpeg' container, Gemini raw 'audio/pcm' samples that carry no rate
 * of their own. A caller that assumed either would play static on the other.
 */
export interface SynthesizedAudio {
    /** Base64. */
    audio: string;
    mimeType: string;
    /** Raw PCM only — a container carries its own rate. */
    sampleRate?: number;
}

/** One normalized POST; the server picks the provider. */
async function synthesizeViaGateway(
    clean: string,
    signal?: AbortSignal,
    timing?: VoiceTiming,
): Promise<SynthesizedAudio> {
    const result = await callCapability<SynthesizedAudio>(
        'speech/synthesize',
        { text: clean },
        signal,
    );
    timing?.mark('tts_body_parsed');
    return result;
}

/**
 * Decode either container to an AudioBuffer the AudioContext can play.
 * Exposed for unit testing — this branch is what keeps a provider switch from
 * playing static, so it is worth pinning down directly.
 */
export async function decodeSynthesized(
    audioContext: AudioContext,
    result: SynthesizedAudio,
): Promise<AudioBuffer> {
    if (result.mimeType !== 'audio/pcm') {
        // Compressed containers (Azure's mp3) go through the platform decoder,
        // which also resamples to the context's rate for us.
        const binary = atob(result.audio);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return audioContext.decodeAudioData(bytes.buffer);
    }
    const channelData = decodePcm16ToFloat32(result.audio);
    const buffer = audioContext.createBuffer(
        NUM_CHANNELS,
        channelData.length,
        result.sampleRate ?? SAMPLE_RATE,
    );
    buffer.getChannelData(0).set(channelData);
    return buffer;
}

/**
 * Synthesize speech from text and play it via AudioContext. Returns a promise
 * that resolves when playback finishes. Throws on API errors.
 *
 * The server picks the voice model — currently Azure
 * gpt-4o-mini-tts, measured at 1.48s against 2.65s for Gemini on the same
 * utterance, with a 105KB mp3 in place of a 180KB PCM buffer.
 */
export async function synthesizeSpeech(
    text: string,
    audioContext: AudioContext,
    signal?: AbortSignal,
    timing?: VoiceTiming,
    onPlaybackStart?: () => void,
): Promise<void> {
    if (!text.trim()) return;

    // Clean up markdown/formatting for more natural speech.
    // Latency scales with output length, so input is capped at 500 chars.
    const clean = text
        .replace(/[*_`#]/g, '')
        .replace(/\n+/g, '. ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 500);

    if (!clean) return;

    timing?.mark('tts_request_start', { chars: clean.length });

    const synthesized = await synthesizeViaGateway(clean, signal, timing);
    if (signal?.aborted) return;

    const audioBuffer = await decodeSynthesized(audioContext, synthesized);
    timing?.mark('tts_decoded', {
        format: synthesized.mimeType,
        samples: audioBuffer.length,
        audioMs: (audioBuffer.duration * 1000).toFixed(0),
    });

    if (signal?.aborted) return;

    // Play via AudioBufferSourceNode
    return new Promise<void>((resolve) => {
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);

        const onAbort = () => {
            try { source.stop(); } catch { /* already stopped */ }
            resolve();
        };
        source.onended = () => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        };
        signal?.addEventListener('abort', onAbort, { once: true });

        source.start();
        timing?.mark('tts_playback_start');
        onPlaybackStart?.();
    });
}
